import React from 'react'

import { faArrowRightLong } from '@fortawesome/pro-regular-svg-icons/faArrowRightLong'
import { Box, styled, Typography } from '@mui/material'
import Image from 'next/image'
import Link from 'next/link'

import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'

import type { PremadeStoryList } from '@/shared/api/models/premadeStoryList'

interface PremadeStoryCatalogCardProps {
  story: PremadeStoryList
}

const PLACEHOLDER_IMAGE = '/images/products/book.webp'

export const PremadeStoryCatalogCard: React.FC<PremadeStoryCatalogCardProps> = ({ story }) => {
  const imageSrc = story.thumbnail_url || story.gallery_images?.[0]?.url || PLACEHOLDER_IMAGE

  return (
    <StyledLink href={`/shop/${story.slug}`}>
      <StyledCard>
        <StyledImageWrapper>
          <Image
            src={imageSrc}
            alt={story.title}
            fill
            sizes="(max-width: 600px) 100vw, (max-width: 900px) 50vw, 33vw"
            style={{ objectFit: 'cover' }}
          />
        </StyledImageWrapper>

        <StyledFooter>
          <StyledTextContent>
            <StyledTitle>{story.title}</StyledTitle>
          </StyledTextContent>
          <StyledArrowIcon icon={faArrowRightLong} className="arrow-icon" />
        </StyledFooter>
      </StyledCard>
    </StyledLink>
  )
}

const StyledLink = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
})

const StyledCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: pxToRem(16),
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  backgroundColor: theme.palette.background.paper,

  '.arrow-icon': {
    transition: 'transform 0.3s ease-in-out',
  },

  '&:hover': {
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },

  '&:hover .arrow-icon': {
    transform: 'translateX(3px)',
  },
}))

const StyledImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
})

const StyledFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  gap: theme.spacing(1),
}))

const StyledTextContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(2),
  minWidth: 0,
})

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: pxToRem(14),
  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(16),
  },
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const StyledArrowIcon = styled(Icon)({
  flexShrink: 0,
})
